import { onCLS, onLCP, onFID, onINP } from 'web-vitals/attribution';
import { findDataComponent } from '../utils/performance-observer-helpers';

const POLL_TIMEOUT = 3000;
const POLL_RETRIES = 5;
const CLS_DECIMALS = 3;
const INTERACTION_EVENTS = [
  'click', 'mousedown', 'mousemove', 'mouseup',
  'keydown', 'keyup', 'touchstart', 'touchmove',
  'scroll', 'wheel', 'contextmenu', 'visibilitychange'
];

let pollCounter = 0;
let longTaskObserver;
let longTaskCount = 0;
let firstInteractionRecorded = false;

const addReportValue = ({ name, value }) => {
  if (typeof window !== 'undefined' && window.newrelic) {
    window.newrelic.setCustomAttribute(name, value);
  }
};

const getQuantumMetricId = () => {
  pollCounter += 1;

  const quantumMetricID = (
    typeof window.QuantumMetricAPI !== 'undefined'
    && typeof window.QuantumMetricAPI.getSessionID === 'function'
    && window.QuantumMetricAPI.getSessionID()
  ) || undefined;

  if (quantumMetricID) {
    addReportValue({ name: 'quantumMetricId', value: quantumMetricID });
    return;
  }

  if (pollCounter > POLL_RETRIES) return;

  setTimeout(getQuantumMetricId, POLL_TIMEOUT);
};

const recordLongTasks = () => {
  longTaskObserver = new PerformanceObserver((list) => {
    const perfEntries = list.getEntries();
    longTaskCount += perfEntries.length;
  });
  longTaskObserver.observe({ entryTypes: ['longtask'] });
};

const recordFirstInteraction = (event) => {
  if (firstInteractionRecorded) return;

  firstInteractionRecorded = true;

  // Handle visibilitychange event specially
  if (event.type === 'visibilitychange' && document.visibilityState !== 'hidden') {
    return; // Only count visibilitychange when page becomes hidden
  }

  addReportValue({ name: 'FirstInteractionType', value: event.type });

  // Force New Relic to send beacon immediately
  if (window.newrelic && typeof window.newrelic.finished === 'function') {
    window.newrelic.finished();
  }

  // Clean up listeners
  INTERACTION_EVENTS.forEach((eventType) => {
    if (eventType === 'visibilitychange') {
      document.removeEventListener(eventType, recordFirstInteraction);
    } else {
      document.removeEventListener(eventType, recordFirstInteraction, { passive: true });
    }
  });

  window.removeEventListener('popstate', recordFirstInteraction);
};

const setupFirstInteractionTracking = () => {
  INTERACTION_EVENTS.forEach((eventType) => {
    if (eventType === 'visibilitychange') {
      document.addEventListener(eventType, recordFirstInteraction);
    } else {
      document.addEventListener(eventType, recordFirstInteraction, { passive: true });
    }
  });

  // Listen for back/forward button navigation (interaction type "popstate")
  window.addEventListener('popstate', recordFirstInteraction);
};

export const performanceObserver = () => {
  setupFirstInteractionTracking();

  onCLS((info) => {
    if (!info || !info.value) return;

    const { entries, attribution } = info;
    const { largestShiftTarget, largestShiftValue, largestShiftEntry } = attribution;

    addReportValue({ name: 'CLS-LargestShiftTarget', value: largestShiftTarget });
    addReportValue({ name: 'CLS-LargestShiftValue', value: largestShiftValue?.toFixed(CLS_DECIMALS) });

    const sourcesOuterHtml = [];
    const dataComponents = [];

    if (Array.isArray(entries)) {
      entries.forEach((entry) => {

        if (Array.isArray(entry.sources)) {
          entry.sources.forEach((source) => {
            if (source?.node) {
              const node = source.node;
              sourcesOuterHtml.push(node.outerHTML);

              const dataComponent = findDataComponent({ node });
              if (dataComponent && !dataComponents.includes(dataComponent)) {
                dataComponents.push(dataComponent);
              }
            }
          });
        }
      });

      addReportValue({ name: 'CLS-Components', value: dataComponents.join(', ') });
      addReportValue({ name: 'CLS-Elements', value: sourcesOuterHtml.filter((html) => html !== '').join(', ') });
    }

    if (!largestShiftEntry || !Array.isArray(largestShiftEntry.sources)) return;

    // eslint-disable-next-line no-restricted-syntax
    for (const { node } of largestShiftEntry.sources) {
      const dataComponent = findDataComponent({ node });
      if (dataComponent) {
        addReportValue({ name: 'CLS-LargestShift-Component', value: dataComponent });
        break;
      }
    }
  });

  onLCP((info) => {
    if (!info || !info.value) return;

    const { lcpEntry, timeToFirstByte } = info.attribution || {};
    const { url, element, renderTime, loadTime } = lcpEntry || {};
    const lcpValueInMilliseconds = renderTime || loadTime || 0;
    const lcpValueInSeconds = lcpValueInMilliseconds / 1000;

    addReportValue({ name: 'LCP-Value', value: lcpValueInSeconds || '' });
    addReportValue({ name: 'LCP-Url', value: url });
    addReportValue({ name: 'LCP-TTFB', value: timeToFirstByte });

    if (element) {
      const dataComponent = findDataComponent({ node: element });
      addReportValue({ name: 'LCP-Component', value: dataComponent });
      addReportValue({ name: 'LCP-Element', value: element.outerHTML });
    }
  });

  onFID((info) => {
    if (longTaskObserver) longTaskObserver.disconnect();
    addReportValue({ name: 'FID-Long-Task-Count', value: longTaskCount });

    if (!info || !info.value) return;

    const { eventEntry, eventTarget } = info.attribution || {};

    if (!eventEntry) return;

    addReportValue({ name: 'FID-TargetElement', value: eventTarget });

    const target = eventEntry.target;

    if (!target) return;

    const dataComponent = findDataComponent({ node: target });
    addReportValue({ name: 'FID-Component', value: dataComponent });
  });

  onINP((info) => {
    if (!info || !info.value) return;

    const { eventTarget, eventEntry } = info.attribution || {};

    if (!eventTarget) return;

    addReportValue({ name: 'INP-TargetElement', value: eventTarget });

    const target = eventEntry.target;

    if (!target) return;

    const dataComponent = findDataComponent({ node: target });
    addReportValue({ name: 'INP-Component', value: dataComponent });
  });

  recordLongTasks();
  setTimeout(getQuantumMetricId, POLL_TIMEOUT);
};